import {
  Autocomplete,
  DialogContent,
  Grid,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Formik } from "formik";
import MDButton from "components/MDButton";
import MDDialog from "components/MDDialog";
import MDSnackbar from "components/MDSnackbar";
import MDInput from "components/MDInput";
import GooglePlacePicker from "components/MDPlacePicker/GooglePlacePicker";
import { createPlaceValidationSchema } from "services/validation";
import { useMaterialUIController } from "context";
import { useEffect, useState } from "react";
import { createPlace } from "store/slice/places/placeSlice";
import { useDispatch, useSelector } from "react-redux";
import CircularIndeterminate from "components/MDLoading";
import { getPlaces } from "store/slice/places/placeSlice";
import { getBrands } from "store/slice/brands/brandSlice";
import { isEmpty } from "lodash";
import { useNavigate } from "react-router-dom";
import { startGuide } from "store/slice/globalControl/globalControlSlice";
import { closeSetupSnackbar } from "store/slice/globalControl/globalControlSlice";
import { setNewlyCreatedPlaceId } from "store/slice/globalControl/globalControlSlice";

const CreatePlace = (props) => {
  const dispatch = useDispatch();
  const { dialogOpen, onClose } = props;
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const brandsList = useSelector((state) => state.brands.brandsList || []);
  const userData = useSelector((state) => state.users?.meInfo);
  const userRole = userData?.roleTitle;
  const [notification, setNotification] = useState({
    color: "",
    icon: "",
    title: "",
    content: "",
    show: false,
    close: () => setNotification({ ...notification, show: false }),
  });

  useEffect(() => {
    if (isEmpty(brandsList)) {
      dispatch(getBrands());
    }
  }, []);

  const handleCreatePlace = (placeData) => {
    setIsLoading(true);
    dispatch(createPlace(placeData))
      .unwrap()
      .then(async (res) => {
        const success = res?.success;
        setIsLoading(false);
        setNotification({
          ...notification,
          color: success ? "success" : "error",
          title: success ? "Success" : "Error",
          content: res?.message,
          icon: success ? "check" : "warning",
          show: true,
        });
        if (success) {
          const newPlaceId = res?.place?._id || res?.data?._id || res?._id || null;
          await dispatch(getPlaces());
          onClose();
          dispatch(closeSetupSnackbar());

          // ── Mark this as a fresh onboarding so places/index only starts guide for new places
          if (newPlaceId) {
            sessionStorage.setItem("newlyCreatedPlaceId", newPlaceId);
            sessionStorage.setItem("onboardingActive", "true");
            dispatch(setNewlyCreatedPlaceId(newPlaceId));
          }

          dispatch(startGuide({ step: "PLACE_SETTING", role: userRole }));
          navigate("/places?guide=PLACE_SETTING", { replace: true });
        }
      })
      .catch((err) => {
        setIsLoading(false);
        setNotification({
          ...notification,
          color: "error",
          title: "Error",
          content: err?.message,
          icon: "warning",
          show: true,
        });
      });
  };

  return (
    <>
      <CircularIndeterminate
        type="full"
        size={20}
        text="Creating Place"
        open={isLoading}
      />

      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        open={notification.show}
        close={notification.close}
        bgWhite
      />

      <MDDialog
        dialogTitle="Create New Place"
        open={dialogOpen}
        dialogClose={onClose}
        closeIcon={true}
        maxWidth="sm"
      >
        <DialogContent>
          <Formik
            initialValues={{
              google: { formatted_address: "" },
              parkingCode: "",
              spaces: null,
              statementDescriptor: "",
              lotName: "",
              brandId: null,
              isGroupedLocation: false,
            }}
            validationSchema={createPlaceValidationSchema}
            onSubmit={(value) => {
              const cleaned = {
                ...value,
                parkingCode: value.parkingCode?.replace(/\s+/g, "") || "",
              };
              handleCreatePlace(cleaned);
            }}
          >
            {(props) => (
              <form onSubmit={props.handleSubmit}>
                <Grid container spacing={2} className="mt-1">
                  {/* Brand */}
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={brandsList || []}
                      disableClearable={false}
                      getOptionLabel={(option) => `${option?.shortBrandName}`}
                      value={
                        brandsList.find(
                          (brand) => brand._id === props.values.brandId,
                        ) || null
                      }
                      onChange={(e, value) =>
                        props.setFieldValue("brandId", value?._id || "")
                      }
                      renderInput={(params) => (
                        <MDInput
                          {...params}
                          label="Select Brand"
                          error={props.errors.brandId && props.touched.brandId}
                          helperText={
                            props.errors.brandId && props.touched.brandId
                              ? props.errors.brandId
                              : null
                          }
                        />
                      )}
                    />
                  </Grid>

                  {/* Parking Code */}
                  <Grid item xs={12} md={6}>
                    <MDInput
                      name="parkingCode"
                      value={props.values.parkingCode}
                      onChange={props.handleChange}
                      onBlur={props.handleBlur}
                      label="Parking Code"
                      error={
                        props.errors.parkingCode && props.touched.parkingCode
                      }
                      helperText={
                        props.errors.parkingCode && props.touched.parkingCode
                          ? props.errors.parkingCode
                          : null
                      }
                    />
                  </Grid>

                  {/* Address */}
                  <Grid item xs={12}>
                    <GooglePlacePicker
                      label="Address"
                      name="google"
                      values={props.values?.google?.formatted_address}
                      errors={props.errors}
                      touched={props.touched}
                      setFieldValue={props.setFieldValue}
                      handleBlur={props.handleBlur}
                      handleChange={props.handleChange}
                    />
                  </Grid>

                  {/* Lot Name */}
                  <Grid item xs={12} md={6}>
                    <MDInput
                      name="lotName"
                      value={props.values.lotName}
                      onChange={props.handleChange}
                      onBlur={props.handleBlur}
                      label="Lot Name"
                      error={props.errors.lotName && props.touched.lotName}
                      helperText={
                        props.errors.lotName && props.touched.lotName
                          ? props.errors.lotName
                          : null
                      }
                    />
                  </Grid>

                  {/* Spaces */}
                  <Grid item xs={12} md={6}>
                    <MDInput
                      name="spaces"
                      type="number"
                      value={props.values.spaces}
                      onChange={props.handleChange}
                      onBlur={props.handleBlur}
                      label="Number of spaces"
                      error={props.errors.spaces && props.touched.spaces}
                      helperText={
                        props.errors.spaces && props.touched.spaces
                          ? props.errors.spaces
                          : null
                      }
                    />
                  </Grid>

                  {/* Statement Descriptor */}
                  <Grid item xs={12}>
                    <MDInput
                      name="statementDescriptor"
                      value={props.values.statementDescriptor}
                      onChange={props.handleChange}
                      onBlur={props.handleBlur}
                      label="Statement Descriptor"
                      error={
                        props.errors.statementDescriptor &&
                        props.touched.statementDescriptor
                      }
                      helperText={
                        props.errors.statementDescriptor &&
                        props.touched.statementDescriptor
                          ? props.errors.statementDescriptor
                          : null
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="isGroupedLocation"
                          checked={props.values.isGroupedLocation}
                          onChange={(e) =>
                            props.setFieldValue(
                              "isGroupedLocation",
                              e.target.checked,
                            )
                          }
                        />
                      }
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontWeight: "400",
                        },
                      }}
                      label="Is that Group Location?"
                    />
                  </Grid>

                  {/* Submit Button */}
                  <Grid item xs={12} className="text-right">
                    <MDButton
                      color={sidenavColor}
                      variant="gradient"
                      type="submit"
                    >
                      Create
                    </MDButton>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </DialogContent>
      </MDDialog>
    </>
  );
};

export default CreatePlace;
